import { useEffect, useState } from 'react';
import { AnimatedSection } from '@/components/ui';
import { BrowserFrame } from '@/components/landing/shared/BrowserFrame';
import { ChatWidget } from '@/components/widget/ChatWidget';
import { ChatCarousel } from '@/components/landing/chat/ChatCarousel';
import { ComponentErrorBoundary } from '@/components/ui/component-error-boundary';

const botId = import.meta.env.VITE_DEMO_BOT_ID as string | undefined;
const hasBotId = !!botId && botId.trim().length > 0;

const useReducedMotion = (): boolean => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
};

export const LiveDemo = () => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  const reducedMotion = useReducedMotion();

  // Fallback — used when env var is missing OR error boundary catches.
  const fallback = <ChatCarousel />;

  // Skeleton — covers the JS mount gap. Suppress animate-pulse if user prefers reduced motion.
  const skeletonClass = reducedMotion
    ? 'h-[500px] bg-muted rounded-xl'
    : 'h-[500px] bg-muted rounded-xl animate-pulse';
  const skeleton = (
    <div role="status" aria-label="Loading live demo..." className={skeletonClass} />
  );

  // Decide what goes inside BrowserFrame:
  //   - no botId  -> fallback (per D-08 condition 1)
  //   - not yet mounted -> skeleton (covers brief client mount gap; SSR/initial render)
  //   - mounted + botId present -> ChatWidget wrapped in ComponentErrorBoundary with fallback
  let widgetSlot: React.ReactNode;
  if (!hasBotId) {
    widgetSlot = fallback;
  } else if (!mounted) {
    widgetSlot = skeleton;
  } else {
    widgetSlot = (
      <ComponentErrorBoundary componentName="LiveDemo ChatWidget" fallback={fallback}>
        <ChatWidget
          inline={true}
          botId={botId}
          botName="ChytBot Assistant"
          welcomeMsg="Hi! Ask me anything about ChytBot — pricing, setup, or how it works."
          avatarEmoji="🤖"
          primaryColor="#7c3aed"
          placeholderText="Ask a question..."
        />
      </ComponentErrorBoundary>
    );
  }

  return (
    <section id="live-demo" className="py-16 md:py-24">
      <div className="container">
        <AnimatedSection className="max-w-2xl mx-auto">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-4">
              See it <span className="aceternity-gradient-text">in action</span>
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              Talk to the AI chatbot right here — ask ChytBot anything about how it works, pricing, or setup. This is a live AI chat online, not a canned demo.
            </p>
          </div>
          <div className="relative h-[500px]">
            <div className="aceternity-orb pointer-events-none absolute -left-20 -bottom-12 h-[22rem] w-[22rem] opacity-25" aria-hidden="true" />
            <div className="aceternity-orb pointer-events-none absolute -right-16 top-8 h-[16rem] w-[16rem] opacity-15" aria-hidden="true" />
            <BrowserFrame>
              {widgetSlot}
            </BrowserFrame>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};
